import { LocationStrategy, NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, inject, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core'

import { NavigationBarState, NavigationBarComponent } from '../navigationbar/navigationbar.component'
import { TacosComponent } from "./tacos/tacos.component"
import { PrivacyComponent } from "./privacy/privacy.component"
import { UnsubscribeComponent } from './unsubscribe/unsubscribe.component'
import { ResponsiveComponent } from '@nx-superprep/shared'
import { ActivatedRoute } from '@angular/router'
import { takeUntil } from 'rxjs'
import { log } from '@nx-superprep/utils';
import { CookieBannerComponent } from "./cookie-banner/cookie-banner.component";

export type LegalType = 'about'|'help'|'tacos'|'privacy'|'unsubscribe'|'cookie'

@Component({
  standalone: true,
  selector: 'pop-ui-legal',
  templateUrl: './legal.component.html',
  styleUrls: ['./legal.component.scss'],
  imports: [NgClass, NavigationBarComponent, TacosComponent, PrivacyComponent, UnsubscribeComponent, CookieBannerComponent]
})
export class LegalComponent extends ResponsiveComponent implements OnInit, OnDestroy {
  legalRoutes = ['privacy', 'tacos', 'unsubscribe', 'cookie'];

  @Input() id: string|null = null
  private _legal: LegalType | undefined = 'tacos'
  @Input()
  public get legal() { return this._legal }
  public set legal(value) {
    if (value) { this._legal = this.legalRoutes.includes(value) ? value : 'tacos' }
    this.legalChange.emit(this._legal);
  }
  @Output() legalChange = new EventEmitter<LegalType>()

  @HostListener('document:click', ['$event'])
  handleLinkClicks(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const link = target.closest('a');

    if (!link) {
      // Handle non-link clicks (like your close button logic)
      if (target.tagName === 'IMG' && target.id === 'close') {
        this.legal = undefined;
      }
      return; // Allow event to propagate
    }

    const href = link.getAttribute('href');

    // Allow mailto links to work normally
    if (href?.startsWith('mailto:')) {
      if (this.isModal) { return; } else { window.location.href = `mailto:${link.pathname}`; }
    }
    // Allow external links to work normally
    if (link.getAttribute('target') === '_blank') {
      if (this.isModal) { return; } else { window.open(link.href, '_blank'); }
    }
    // Parse the URL to check if it's one of our internal legal routes
    try {
      // Only process absolute URLs - for relative URLs let browser handle it
      if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
        const url = new URL(link.href);
        // Check if this is one of our internal legal routes
        if (url.hostname === 'www.longtailedu.com') {
          const path = url.pathname.substring(1); // Remove leading slash

          if (this.legalRoutes.includes(path)) {
            event.preventDefault(); // Prevent default only for our internal routes
            this.legal = path as LegalType;
            return;
          }
        }
      }
    } catch (e) {
      return;
    }
  }

  private _navigationState: NavigationBarState = {
    current: { route: 'legal', type: document.URL.endsWith('/') ? 'close-end' : 'root' },
    title: '',
  }
  get navigationState() {
    return this._navigationState
  }
  set navigationState(value) {
    this._navigationState = value
  }

  isModal = false

  override ngOnInit() {
    super.ngOnInit()
    //log.debug(this.elementRef)
    const container = this.elementRef.nativeElement.parentElement.parentElement.parentElement as Element
    this.renderer.addClass(container, 'noscrollbar')
    this.route.paramMap
    .pipe(takeUntil(this.onDestroy))
    .subscribe(params => {
      this.id ??= params.get('id')
      this.isModal = !this.id
      if (!this.legal) { this.legal = (this.id && this.legalRoutes.includes(this.id)) ? this.id as LegalType : 'tacos' }
      this.location.replaceState(null, '', '/', '')
    })
  }

   readonly elementRef = inject(ElementRef)
   readonly renderer = inject(Renderer2)
   readonly location = inject(LocationStrategy)
   readonly route = inject(ActivatedRoute)

  constructor() {
    super()
    this.rootClassName = 'legal-root-class-name'
  }
}
